// ════════════════════════════════════════════════════════════
// Auth types — ECR
// ════════════════════════════════════════════════════════════
// Type aliases au-dessus des types Supabase générés, pour
// éviter de propager `Database['public']['Tables']...` partout.
// ════════════════════════════════════════════════════════════
import type { Tables, Enums } from '@/types/supabase';

export type Profile = Tables<'profiles'>;
export type Tenant = Tables<'tenants'>;
export type AppRole = Enums<'app_role'>;

// ─── Labels FR pour affichage UI ────────────────────────────
export const ROLE_LABELS: Record<AppRole, string> = {
  daf: 'Directeur Administratif et Financier',
  dg: 'Directeur Général',
  responsable_financiere: 'Responsable Financière',
  comptable: 'Comptable',
  ops: 'Opérations',
  pointeur: 'Pointeur',
  directeur_production: 'Directeur de Production',
};

// ─── Labels courts (pour badges, listes) ────────────────────
export const ROLE_SHORT_LABELS: Record<AppRole, string> = {
  daf: 'DAF',
  dg: 'DG',
  responsable_financiere: 'Resp. Fin.',
  comptable: 'Comptable',
  ops: 'Ops',
  pointeur: 'Pointeur',
  directeur_production: 'Dir. Prod.',
};

// ─── Accès module par rôle ──────────────────────────────────
// ECR : 3 rôles actifs seulement — daf (Sidi + Abidine), responsable
// financière (Fatimetou) et comptable. Tous voient dépenses,
// encaissements ET trésorerie. Les autres rôles de l'enum (dg, ops,
// pointeur, directeur_production) sont hérités de Mauripe et n'ont
// aucune surface dans ECR.
// Clés = préfixes de route de premier niveau.
export const MODULE_ACCESS: Record<AppRole, readonly string[] | 'all'> = {
  daf: 'all',
  dg: [],
  responsable_financiere: ['/', '/depenses', '/paiements', '/tresorerie'],
  comptable: ['/', '/depenses', '/paiements', '/tresorerie'],
  ops: [],
  pointeur: [],
  directeur_production: [],
};

// Routes réservées au DAF uniquement, même pour les rôles 'all'
// (le DG n'a pas accès à l'administration des utilisateurs).
const DAF_ONLY_TOP = new Set(['/admin']);

// Routes accessibles à TOUT utilisateur authentifié, quel que soit son rôle
// (paramètres de compte perso : self-service mot de passe).
const ALWAYS_ALLOWED_TOP = new Set(['/compte']);

/** Le rôle a-t-il accès à cette route de module (top-level) ? */
export function canAccessRoute(role: AppRole | null | undefined, to: string): boolean {
  if (!role) return false;
  if (DAF_ONLY_TOP.has(to)) return role === 'daf';
  if (ALWAYS_ALLOWED_TOP.has(to)) return true;
  const acc = MODULE_ACCESS[role];
  if (acc === 'all') return true;
  return acc.includes(to);
}

/**
 * Garde-fou réel : le rôle a-t-il accès à ce chemin COMPLET ?
 * Vérifie le module top-level (pas de restrictions de sous-routes
 * dans ECR light).
 */
export function canAccessPath(role: AppRole | null | undefined, pathname: string): boolean {
  if (!role) return false;

  const seg = pathname.split('/').filter(Boolean); // '/depenses/new' -> ['depenses','new']
  const topKey = seg.length === 0 ? '/' : `/${seg[0]}`;

  // Routes DAF-only (ex. /admin) : refusées même aux rôles 'all' (DG).
  if (DAF_ONLY_TOP.has(topKey)) return role === 'daf';

  // Routes perso (ex. /compte) : ouvertes à tout user authentifié.
  if (ALWAYS_ALLOWED_TOP.has(topKey)) return true;

  const acc = MODULE_ACCESS[role];
  if (acc === 'all') return true;
  return acc.includes(topKey);
}

/** Première route accessible (landing) pour un rôle. */
export function landingRouteForRole(role: AppRole | null | undefined): string {
  if (!role) return '/';
  const acc = MODULE_ACCESS[role];
  if (acc === 'all') return '/';
  return acc[0] ?? '/compte';
}

// ─── Type guards utiles ─────────────────────────────────────
export const isDaf = (role: AppRole | null | undefined): role is 'daf' => role === 'daf';

export const isAdminRole = (role: AppRole | null | undefined): boolean =>
  role === 'daf' || role === 'dg';

export const isFinancialRole = (role: AppRole | null | undefined): boolean =>
  role === 'daf' || role === 'responsable_financiere' || role === 'comptable';
